import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"

import { Icon } from "../icons/Icon"
import clsx from "clsx"
import { analytics } from "@/telemetry/amplitude"
import { toTestId } from "./testid"

const Checkbox = React.forwardRef<
	React.ElementRef<typeof CheckboxPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
		state?: true | false | "indeterminate"
		trackingLabel?: string
	}
>(({ className, state, trackingLabel, onClick, onCheckedChange, ...props }, ref) => {
	let target: EventTarget | undefined
	return (
		<CheckboxPrimitive.Root
			aria-label={trackingLabel}
			data-testid={trackingLabel ? toTestId(trackingLabel) : undefined}
			onClick={(e) => {
				if (!trackingLabel) return
				onClick?.(e)
				target = e.target
			}}
			onCheckedChange={(checked) => {
				onCheckedChange?.(checked)
				if (!trackingLabel) return
				analytics.trackEvent(
					{
						label: trackingLabel,
						type: "click",
						value: checked ? "checked" : "unchecked",
					},
					target,
				)
			}}
			ref={ref}
			className={clsx(
				"ring-offset-background focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground peer size-4 shrink-0 rounded-sm border border-base-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
				className,
			)}
			{...props}
		>
			<CheckboxPrimitive.Indicator
				className={"flex items-center justify-center text-current"}
			>
				{(!state || state !== "indeterminate") && <Icon name="check" size={12} />}
				{state === "indeterminate" && <Icon name="indeterminate" size={12} />}
			</CheckboxPrimitive.Indicator>
		</CheckboxPrimitive.Root>
	)
})
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }

export const CheckboxItem = <T extends string>(props: {
	id: T
	checkedState: true | false | "indeterminate"
	content: React.ReactNode
	onCheckChanged: (id: T, isChecked: boolean) => void
	indent?: boolean
	trackingLabel: string
	disabled?: boolean
}) => {
	return (
		<div
			className={clsx(
				props.indent === true && "pl-4",
				props.disabled && "pointer-events-none",
				"flex flex-row items-center space-x-2",
			)}
			onClick={() => {
				if (props.checkedState === "indeterminate") {
					props.onCheckChanged(props.id, true)
				} else {
					props.onCheckChanged(props.id, !props.checkedState)
				}
			}}
		>
			<Checkbox
				trackingLabel={props.trackingLabel}
				checked={props.checkedState !== false}
				state={props.checkedState}
				disabled={props.disabled}
			/>
			<div className="truncate text-sm">{props.content}</div>
		</div>
	)
}
